import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { IftaLabelModule } from 'primeng/iftalabel';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { CustomerItem } from '../customer-item.model';
import { CustomerItemService } from '../customer-item.service';
import { CustomersService } from '../../customers/customers.service';
import { Customer } from '../../customers/customers.model';
import { ItemService } from '../../items/item.service';
import { Item } from '../../items/item.model';
import { BrandsService } from '../../brands/brands.service';
import { Brand } from '../../brands/brands.model';
import { SubBrandsService } from '../../brands/sub-brands/sub-brands.service';
import { SubBrand } from '../../brands/sub-brands/sub-brands.model';
import { EntityDropdownPickerComponent } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { CustomerPickerSummary } from '../../customers/customers.model';

@Component({
  selector: 'app-customer-item-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    InputTextarea,
    IftaLabelModule,
    ToastModule,
    EntityDropdownPickerComponent,
  ],
  providers: [MessageService],
  templateUrl: './customer-item-form.component.html',
  styleUrl: './customer-item-form.component.css',
})
export class CustomerItemFormComponent implements OnInit {
  customerItemForm!: FormGroup;
  customerItemId: number | null = null;
  isEditMode = false;
  pageTitle = 'Create Customer Item';

  customerPickerAdapter!: EntityDropdownPickerAdapter<CustomerPickerSummary, Customer>;
  tamerItemPickerAdapter!: EntityDropdownPickerAdapter<Item>;
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;
  subBrandPickerAdapter!: EntityDropdownPickerAdapter<SubBrand>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private customerItemService: CustomerItemService,
    private customersService: CustomersService,
    private itemService: ItemService,
    private brandsService: BrandsService,
    private subBrandsService: SubBrandsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.customerPickerAdapter = this.customersService.createCustomerPickerAdapter();
    this.tamerItemPickerAdapter = this.itemService.createItemPickerAdapter();
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.subBrandPickerAdapter = this.subBrandsService.createSubBrandPickerAdapter();

    this.initForm();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.customerItemId = +id;
      this.isEditMode = true;
      this.pageTitle = 'Edit Customer Item';
      this.loadCustomerItemData(this.customerItemId);
    }

    this.customerItemForm.get('tamerItem')?.valueChanges.subscribe((item) => {
      this.applyTamerItemDerivedValues(item ?? null);
    });
  }

  private initForm(): void {
    this.customerItemForm = this.fb.group({
      customerItemId: [''],
      customerItemNumber: ['', Validators.required],
      customerItemDescription: [''],
      customer: [null, Validators.required],
      tamerItem: [null, Validators.required],
      brand: [null],
      subBrand: [null],
      customerItemUom: [''],
      itemUom: [''],
      customerItemPrice: [null],
      caseCount: [null],
      palletCount: [null],
    });
  }

  private loadCustomerItemData(id: number): void {
    this.customerItemService.findById(id).subscribe({
      next: (customerItem) => {
        this.customerItemForm.patchValue({
          ...customerItem,
          customer: customerItem.customer ?? null,
          tamerItem: customerItem.tamerItem ?? null,
          brand: customerItem.brand ?? null,
          subBrand: customerItem.subBrand ?? null,
        });

        this.applyTamerItemDerivedValues(customerItem.tamerItem ?? null);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customer item data.',
        });
        console.error(err);
      },
    });
  }

  private applyTamerItemDerivedValues(item: Item | null | undefined): void {
    if (!item) {
      this.customerItemForm.patchValue({
        brand: null,
        subBrand: null,
        itemUom: '',
      });
      return;
    }

    this.customerItemForm.patchValue({
      brand: item.brand ?? null,
      subBrand: item.subBrand ?? null,
      itemUom: item.erpItemUom ?? '',
    });
  }

  saveCustomerItem(): void {
    if (this.customerItemForm.invalid) {
      this.customerItemForm.markAllAsTouched();
      return;
    }

    const formValue = this.customerItemForm.value;
    const customerItem: CustomerItem = {
      customerItemId: this.customerItemId ?? undefined,
      customerItemNumber: formValue.customerItemNumber,
      customerItemDescription: formValue.customerItemDescription,
      customer: formValue.customer,
      tamerItem: formValue.tamerItem,
      brand: formValue.brand,
      subBrand: formValue.subBrand,
      customerItemUom: formValue.customerItemUom,
      itemUom: formValue.itemUom,
      customerItemPrice: formValue.customerItemPrice,
      caseCount: formValue.caseCount,
      palletCount: formValue.palletCount,
    };

    const operation = this.isEditMode
      ? this.customerItemService.update(this.customerItemId!, customerItem)
      : this.customerItemService.create(customerItem);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Customer item ${this.isEditMode ? 'updated' : 'created'} successfully.`,
        });
        setTimeout(() => this.cancel(), 1200);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} customer item.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['customer-item', 'list'] } },
    ]);
  }
}
